import {
	HocuspocusProvider,
	type HocuspocusProviderWebsocket,
} from "@hocuspocus/provider";
import { type Signal, signal } from "@preact-signals/safe-react";

import config from "@src/config";
import { Doc } from "yjs";
import {
	clearAuthRetryState,
	createOnAuthenticationFailed,
	createOnClose,
	createOnDestroy,
	createOnSynced,
} from "./auth-retry";
import type { SyncStatus } from "./auth-retry";
import type { WhiteboardDocumentId } from "./document-id";
import type { DocumentId } from "./document-id/base-document-id";
import { hasLocalDocumentContent, watchDocumentReady } from "./document-ready";
import {
	IndexeddbPersistence,
	clearAllLocalDocuments,
} from "./indexeddb-persistence";
import {
	clearLocallyCreatedDocuments,
	consumeLocallyCreatedDocument,
	isLocalFirstYjsEnabled,
} from "./local-first-settings";
import { resolveCollaborationToken } from "./y-auth-credentials";

type ProviderEntry = {
	id: DocumentId;
	provider: HocuspocusProvider;
	localProvider?: IndexeddbPersistence;
	syncedState: Signal<SyncStatus>;
	accessToken: string;
	refreshedJwt?: string;
	recreateProvider?: () => void;
};

const providers = new Map<string, ProviderEntry>();

const boardDocumentMap = new Map<string, Set<DocumentId>>();

export function getDocument(
	socket: HocuspocusProviderWebsocket,
	accessToken: string,
	documentIds: {
		whiteboardId?: WhiteboardDocumentId;
		documentId: DocumentId;
	},
	options?: {
		recreateProvider?: () => void;
	},
) {
	const { documentId, whiteboardId } = documentIds;
	const { recreateProvider } = options ?? {};
	const documentIdString = documentId.toString();
	const existingProvider = providers.get(documentIdString);
	if (existingProvider) {
		if (accessToken && accessToken !== existingProvider.accessToken) {
			existingProvider.accessToken = accessToken;
			existingProvider.refreshedJwt = undefined;
		}
		if (recreateProvider) {
			existingProvider.recreateProvider = recreateProvider;
		}
		return existingProvider;
	}

	const document = new Doc();
	const syncedStateSignal = signal<SyncStatus>("pending");
	const localFirstEnabled = isLocalFirstYjsEnabled();

	if (localFirstEnabled && consumeLocallyCreatedDocument(documentIdString)) {
		syncedStateSignal.value = "synced-local";
	}

	let localProvider: IndexeddbPersistence | undefined;

	// Instantiate HocuspocusProvider before IndexeddbPersistence so its Y.Doc
	// `update` observer is registered first and remote sync is never blocked by
	// local IndexedDB state.
	const provider = new HocuspocusProvider({
		document,
		websocketProvider: socket,
		name: documentIdString,
		token: () => {
			const current = providers.get(documentIdString);
			return resolveCollaborationToken(
				current ? current.accessToken : accessToken,
				whiteboardId,
				current?.refreshedJwt,
			);
		},
		forceSyncInterval: 30000,
		onSynced: createOnSynced(documentId, syncedStateSignal),
		onClose: createOnClose(syncedStateSignal, document),
		onAuthenticationFailed: createOnAuthenticationFailed(
			documentId,
			syncedStateSignal,
			(refreshedToken) => {
				const current = providers.get(documentIdString);
				if (current && refreshedToken !== undefined) {
					current.refreshedJwt = refreshedToken;
				}
				if (provider.isConnected) {
					provider.disconnect();
				}
				void provider.connect();
				current?.recreateProvider?.();
			},
			() => {
				void localProvider?.clearData();
			},
		),
		onDestroy: () => {
			removeDocumentReadyWatcher();
			createOnDestroy(syncedStateSignal)();
		},
	});

	if (config.features.localSync || localFirstEnabled) {
		localProvider = new IndexeddbPersistence(documentIdString, document);
		if (localFirstEnabled) {
			localProvider.on("synced", () => {
				if (
					syncedStateSignal.value === "pending" &&
					hasLocalDocumentContent(document)
				) {
					syncedStateSignal.value = "synced-local";
				}
			});
		}
	}

	const removeDocumentReadyWatcher = watchDocumentReady(
		document,
		syncedStateSignal,
		localProvider,
	);

	if (whiteboardId) {
		const boardDocuments =
			boardDocumentMap.get(whiteboardId.toString()) || new Set();
		boardDocuments.add(documentId);
		boardDocumentMap.set(whiteboardId.toString(), boardDocuments);
	}

	const entry: ProviderEntry = {
		id: documentId,
		provider,
		localProvider,
		syncedState: syncedStateSignal,
		accessToken,
		refreshedJwt: undefined,
		recreateProvider,
	};
	providers.set(documentIdString, entry);

	return entry;
}

export function getExistingDocument(documentIdString: string): Doc | undefined {
	return providers.get(documentIdString)?.provider.document;
}

export function clearBoardDocuments(whiteboardId: WhiteboardDocumentId) {
	const boardDocuments = boardDocumentMap.get(whiteboardId.toString());
	if (boardDocuments) {
		boardDocuments.forEach((documentId) => {
			const docIdString = documentId.toString();
			const provider = providers.get(docIdString);
			if (provider) {
				provider.provider.destroy();
				providers.delete(docIdString);
			}

			if (provider?.localProvider) {
				provider.localProvider.destroy();
			}

			clearAuthRetryState(docIdString);
			boardDocumentMap.delete(whiteboardId.toString());
			providers.delete(docIdString);
		});
	}
}

export function clearAllDocuments(): Promise<void> {
	for (const entry of providers.values()) {
		entry.provider.destroy();
		void entry.localProvider?.destroy();
	}
	providers.clear();
	boardDocumentMap.clear();
	clearLocallyCreatedDocuments();
	return clearAllLocalDocuments();
}
